type RowWithData = { data: string };

/**
 * Diff rows keyed by serialized `data` (Issue / Machine cache rows).
 * Deletes DB rows whose `data` is not in the incoming set; adds incoming rows not already stored.
 */
export function diffByRowDataEquality<
  TDb extends RowWithData & { id?: unknown },
  TNew extends RowWithData
>(
  dbRows: TDb[],
  incomingRows: TNew[],
  idSelector: (row: TDb) => string = (row) => String(row.id)
): { toAdd: TNew[]; toDeleteIds: string[] } {
  const incomingData = new Set(incomingRows.map((row) => row.data));
  const dbData = new Set(dbRows.map((row) => row.data));

  const toDeleteIds = dbRows
    .filter((row) => !incomingData.has(row.data))
    .map(idSelector);

  const toAdd = incomingRows.filter((row) => !dbData.has(row.data));

  return { toAdd, toDeleteIds };
}

/**
 * Diff by primary id: remove DB ids missing from incoming; upsert when `data` changed or row is new.
 */
export function diffByIdAndDataField<
  TDb extends { id: string | number; data: string },
  TNew extends { id: string | number; data: string }
>(dbRows: TDb[], incomingRows: TNew[]): { toAdd: TNew[]; toDeleteIds: string[] } {
  const incomingById = new Map(incomingRows.map((row) => [String(row.id), row]));
  const dbById = new Map(dbRows.map((row) => [String(row.id), row]));

  const toDeleteIds = dbRows
    .filter((row) => !incomingById.has(String(row.id)))
    .map((row) => String(row.id));

  const toAdd = incomingRows.filter((row) => {
    const existing = dbById.get(String(row.id));
    return !existing || existing.data !== row.data;
  });

  return { toAdd, toDeleteIds };
}

/**
 * Diff scoped document rows by serialized `data` within a folder/machine scope.
 */
export function diffByDataFieldSet<
  TDb extends RowWithData & { id?: unknown },
  TNew extends RowWithData
>(
  dbRows: TDb[],
  incomingRows: TNew[],
  idSelector: (row: TDb) => string = (row) => String(row.id)
): { toAdd: TNew[]; toDeleteIds: string[] } {
  return diffByRowDataEquality(dbRows, incomingRows, idSelector);
}

/**
 * DB rows whose id is not present in the incoming id set (Project list eviction).
 */
export function idsAbsentFromIncoming<T extends { id: unknown }>(
  dbRows: T[],
  incomingIds: Set<string>
): T[] {
  return dbRows.filter((row) => !incomingIds.has(String(row.id)));
}
